import React from 'react';
import { ArrowRight } from 'lucide-react';
import { CTA_LABEL, CTA_SUBLINE, CTA_SUBLINE_SHORT, bookingUrl } from '../site';

// The band the button sits on, because the focus ring's offset is a hole punched in
// that band: the wrong value reads as a dark halo around a focused control.
type Band = 'gray-800' | 'gray-900' | 'gray-950';

type Variant = 'hero' | 'section' | 'bare' | 'nav' | 'dialog' | 'inline';

// Which practice's offer this is. Only the inline variant carries it: a filled button
// is always the primary action and always the accent.
type Tone = 'accent' | 'cloud';

const OFFSET: Record<Band, string> = {
  'gray-800': 'focus-visible:ring-offset-gray-800',
  'gray-900': 'focus-visible:ring-offset-gray-900',
  'gray-950': 'focus-visible:ring-offset-gray-950',
};

const SIZE: Record<Exclude<Variant, 'inline'>, string> = {
  hero: 'px-6 py-3 text-base',
  section: 'px-6 py-3 text-base',
  bare: 'px-6 py-3 text-base',
  nav: 'px-4 py-2 text-sm',
  dialog: 'px-5 py-2.5 text-sm',
};

// Only the placements that stand alone carry the promise; where the section already
// says it in its own lead paragraph, repeating it under the button is duplicated copy.
const SUBLINE: Partial<Record<Variant, string>> = {
  hero: CTA_SUBLINE_SHORT,
  section: CTA_SUBLINE,
};

const WITH_ARROW: Variant[] = ['hero', 'dialog', 'inline'];

// accent-400 measures 4.44:1 on gray-800 and fails; accent-300 is the band's answer.
// Encoded here so the rule cannot be forgotten at the next placement.
const INLINE_TEXT: Record<Tone, Record<Band, string>> = {
  accent: {
    'gray-800': 'text-accent-300 hover:text-accent-200',
    'gray-900': 'text-accent-400 hover:text-accent-300',
    'gray-950': 'text-accent-400 hover:text-accent-300',
  },
  // The AWS practice is structural, never the primary action, so its booking link is
  // the only one that does not wear the accent.
  cloud: {
    'gray-800': 'text-cloud-300 hover:text-cloud-200',
    'gray-900': 'text-cloud-300 hover:text-cloud-200',
    'gray-950': 'text-cloud-300 hover:text-cloud-200',
  },
};

interface BookAssessmentProps {
  placement: string;
  variant?: Variant;
  band?: Band;
  tone?: Tone;
  label?: string;
  className?: string;
}

const BookAssessment: React.FC<BookAssessmentProps> = ({
  placement,
  variant = 'section',
  band = 'gray-900',
  tone = 'accent',
  label = CTA_LABEL,
  className = '',
}) => {
  const arrow = WITH_ARROW.includes(variant) ? (
    <ArrowRight className={variant === 'inline' ? 'ml-1 h-4 w-4' : 'ml-2 h-4 w-4'} aria-hidden="true" />
  ) : null;

  if (variant === 'inline') {
    return (
      <a
        href={bookingUrl(placement)}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex min-h-[44px] items-center py-2.5 font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 ${INLINE_TEXT[tone][band]} ${className}`}
      >
        {label}
        {arrow}
      </a>
    );
  }

  const subline = SUBLINE[variant];

  const button = (
    <a
      href={bookingUrl(placement)}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex min-h-[44px] items-center justify-center rounded-md bg-accent-500 font-medium text-gray-950 transition-colors duration-150 hover:bg-accent-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 ${SIZE[variant]} ${OFFSET[band]} ${subline ? '' : className}`}
    >
      {label}
      {arrow}
    </a>
  );

  // Without a subline there is nothing to wrap, and a wrapper would stop the button
  // stretching where its parent is a stacked flex row (the closing CTA on mobile).
  if (!subline) {
    return button;
  }

  return (
    <div className={className}>
      {button}
      <p className="mt-3 max-w-measure text-sm text-gray-400">{subline}</p>
    </div>
  );
};

export default BookAssessment;
